import * as React from 'react';
import './itemUpdateModal.scss';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { Buttons, Cancel, Primary, Secondary } from 'rcd-cpp-ui/ui/button';
import { Error } from 'rcd-cpp-ui/ui/alert';
import { ConfigureLightUpdateWorkflow } from '../MetadataForm/ConfigureWorkflowField/configureLightUpdateWorkflow';
import { useWorkflowModal } from '../MetadataForm/ConfigureWorkflowField/useWorkflowModal';
import { useActions } from './itemUpdateModalActions';
import { UpdateOptions } from '../../enums/UpdateOptions';
import { useLoader } from '../Loader/loader';
import { useActions as useMetadataActions } from '../../components/MetadataForm/metadataFormActions';
import { ToasterWithLink, useToaster } from '../Toaster/toaster';
import URLManager from '../../utils/urlManager';
import { useFullStory } from '../App/fullStory';
import { FSEvent } from '../../enums/FSEvent';
import { Metadata, Schema } from '../../types';
import { ItemLink } from '../ItemLink/itemLink';
import { getItemContentTypeId } from '../../utils';
import { ContentType } from '../../enums/ContentType';
import { Card } from '../Card/card';

type Props = {
  onCancel: () => any;
  data: Partial<Metadata>;
  schemaForm: Schema;
  schema: Schema;
};

export const ItemUpdateModal = ({
  onCancel,
  data,
  schemaForm,
  schema
}: Props) => {
  const [selected, setSelected] = React.useState(null);
  const [itemInfo, setItemInfo] = React.useState(null);
  const [showLightUpdateConfigure, setShowLightUpdateConfigure] =
    React.useState(false);
  const { createUNI, ...actions } = useActions();
  const { isLoading, Loader, loadItemInfo, createLightUpdate } =
    useLoader(actions);
  const { showForm } = useMetadataActions();
  const { updateUrl } = URLManager.useRouter();
  const { captureEvent } = useFullStory();
  const { createToaster } = useToaster(ToasterWithLink, { updateUrl });
  const itemContentTypeId = getItemContentTypeId(data);
  const itemId = data?.props?.itemId;
  const lightUpdateConfigData = React.useMemo(
    () => ({
      ...data,
      props: {
        ...data?.props,
        configureLightUpdateWorkflowType: null,
        configureLightUpdateWorkflowSkipIds: null
      }
    }),
    [data]
  );

  const closeConfigureModal = React.useCallback(() => {
    setShowLightUpdateConfigure(false);
    onCancel();
  }, [onCancel]);

  const {
    workflowState: lightUpdateWorkflowState,
    setWorkflowState: setLightUpdateWorkflowState,
    onWorkflowStateChange: onLightUpdateWorkflowStateChange,
    isSaving,
    isSaveDisabled,
    setIsSaveDisabled,
    errorMessage,
    setHasChanges,
    setIsModalDisabled,
    handleClose: handleLightUpdateConfigureClose,
    ConfirmModal,
    wrapSave
  } = useWorkflowModal({
    initialState: {
      configureWorkflowSkipIds: null,
      configureWorkflowType: null,
      requestCreativeHelp: false
    },
    onClose: closeConfigureModal
  });

  const handleLightUpdateSave = React.useCallback(
    wrapSave(
      async () => {
        captureEvent(FSEvent.LightUpdate);
        await createLightUpdate(itemInfo, data, createToaster(), {
          configureLightUpdateWorkflowType:
            lightUpdateWorkflowState.configureWorkflowType,
          requestCreativeHelp: lightUpdateWorkflowState.requestCreativeHelp,
          configureLightUpdateWorkflowSkipIds:
            lightUpdateWorkflowState.configureWorkflowSkipIds
        });
      },
      {
        errorMessage:
          'Failed to save light update workflow configuration. Please try again.',
        onSuccess: closeConfigureModal
      }
    ),
    [
      wrapSave,
      actions,
      data,
      lightUpdateWorkflowState,
      captureEvent,
      createLightUpdate,
      itemInfo,
      createToaster
    ]
  );

  React.useEffect(() => {
    if (itemId) {
      loadItemInfo(itemId).then(itemInfo => {
        setItemInfo(itemInfo);
      });
    }
  }, [itemId]);
  const uniBy: Metadata = data?.props?.uniBy?.filter(i => i.props?.refId)[0];
  const showUniError = selected == UpdateOptions.UNI && !!uniBy;

  const disabledContentType =
    itemContentTypeId == ContentType.Radar_Profile ||
    itemContentTypeId == ContentType.INNOVATION_PROFILE;

  if (showLightUpdateConfigure) {
    return (
      <>
        <Modal
          header="Configure Light Update Workflow"
          id="configureWorkflowModal"
          onClose={handleLightUpdateConfigureClose}
          footer={
            <Buttons>
              <Secondary
                label="Cancel"
                onClick={handleLightUpdateConfigureClose}
              />
              <Primary
                label="Save"
                disabled={isSaveDisabled}
                onClick={handleLightUpdateSave}
              />
            </Buttons>
          }
        >
          <ConfigureLightUpdateWorkflow
            data={lightUpdateConfigData}
            onClose={handleLightUpdateConfigureClose}
            onSave={() => Promise.resolve()}
            workflowState={lightUpdateWorkflowState}
            onWorkflowStateChange={onLightUpdateWorkflowStateChange}
            onSaveDisabledChange={setIsSaveDisabled}
            errorMessage={errorMessage}
            onHasChangesChange={setHasChanges}
            onIsModalDisabledChange={setIsModalDisabled}
            isSaving={isSaving}
            isNewLightUpdate={true}
          />
        </Modal>
        <ConfirmModal />
      </>
    );
  }

  return (
    <Modal
      header="Update"
      id="itemUpdateModal"
      onClose={onCancel}
      footer={
        <Buttons>
          <Cancel onClick={onCancel} />
          <Primary
            label={`${selected != UpdateOptions.UNI ? 'Save' : 'Next'}`}
            disabled={
              isLoading || !selected || itemInfo == null || showUniError
            }
            onClick={() => {
              switch (selected) {
                case UpdateOptions.UNI:
                  updateUrl({ id: 'newitem' });
                  showForm(createUNI(data, schemaForm), schema);
                  onCancel();
                  break;
                case UpdateOptions.LightUpdate:
                  setShowLightUpdateConfigure(true);
                  break;
              }
            }}
          />
        </Buttons>
      }
    >
      <Loader />
      <div>Choose the appropriate type of update:</div>
      <div className="item-update-guidelines">
        Review guidelines for each option:
        {UpdateOptionsList.map(({ name, guidelineLink }) => (
          <span key={name}>
            <a href={guidelineLink} target="_blank">
              {name}
            </a>
          </span>
        ))}
      </div>
      <div className="item-update-options scrollable">
        {UpdateOptionsList.map(
          ({ name, description, additionalText, endText }) => (
            <>
              <Card
                onSelect={() => setSelected(name)}
                selected={selected == name}
                disabled={
                  !itemInfo ||
                  !itemInfo.permissions[name] ||
                  disabledContentType
                }
                key={name}
              >
                <div>
                  <b>{name}: </b>
                  {description}
                </div>
                <ul>
                  {additionalText.map(({ value, label }) => (
                    <li key={label}>
                      <b>{label}</b>
                      {value}
                    </li>
                  ))}
                </ul>
                {endText}
              </Card>
              {name == UpdateOptions.UNI && showUniError && (
                <Error>
                  This document already has an UNI:{' '}
                  <ItemLink
                    refId={uniBy?.props?.refId}
                    title={uniBy?.props?.title}
                    onClick={onCancel}
                  />
                </Error>
              )}
            </>
          )
        )}
      </div>
    </Modal>
  );
};

const lightUpdateEndText = () => {
  return (
    <>
      <div>
        Note: This Light Update guidance applies for all regular content; if you
        are updating content designated as{' '}
        <a
          target="_blank"
          href="https://gartner365.sharepoint.com/:p:/r/sites/ProjGravityRAThoughtLeadership/Shared%20Documents/General/Beta%20Research/Evolving%20Insights%20Resources/Evolving%20Insights%20-%20When%20and%20How%20to%20Use.pptx?d=w0370776876274a0fab7c2656dbb9ec0d&csf=1&web=1&e=g0Pe1t"
        >
          Evolving Insights
        </a>{' '}
        however, there are no limitations in the scope of the changes allowed
        (e.g., positional changes are permitted).
      </div>
    </>
  );
};

const UpdateOptionsList = [
  {
    description:
      'A new iteration of a research piece that, when published on gartner.com, automatically archives the prior version. Archived content appears lower in search results and redirects readers to the UNI.',
    name: UpdateOptions.UNI,
    guidelineLink:
      'https://docs.google.com/document/d/1hJl25OKedcLAmdWPSP-uz_VC0Od0L_aFnUNnlggDIzk/edit',
    additionalText: [
      {
        label: 'Use: ',
        value:
          'To update research with new data, insights or perspectives, update any evaluative content'
      },
      {
        label: 'Do not use: ',
        value: 'To correct errors in a published piece'
      }
    ]
  },
  {
    description:
      'A limited update to a published research piece to enhance its position and extend its active window. Available for specific content types only. "Suggesting" mode must be on before changing content.',
    name: UpdateOptions.LightUpdate,
    guidelineLink:
      'https://docs.google.com/presentation/d/1G-gGEPznq4oAaYG1fFJvj9BIRK6f-9px0AANluDZzHg/edit?usp=sharing',
    additionalText: [
      {
        label: 'Use: ',
        value:
          'For small, specific changes or enhancements that do not change our position'
      },
      {
        label: 'Do not use: ',
        value: 'To change our position with new data, insights or perspectives'
      }
    ],
    endText: lightUpdateEndText()
  }
];
